import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Building2, Phone, Mail, Bell } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPriceCompact } from '@/lib/formatPrice';

interface ProjectWithImages {
  id: string;
  name: any;
  city: string;
  sub_city: string | null;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
  status: string | null;
  total_units: number | null;
  available_units: number | null;
  slug: string | null;
  is_featured: boolean | null;
  developer_id: string;
  project_images: { url: string; is_primary: boolean | null }[];
  developer_profiles: { company_name: string; logo_url: string | null } | null;
}

// (formatter now uses global currency context inside component)

function getStatusColor(status: string | null) {
  switch (status) {
    case 'ready': return 'bg-emerald-500';
    case 'under_construction': return 'bg-amber-500';
    case 'upcoming': return 'bg-blue-500';
    default: return 'bg-muted';
  }
}

function getStatusLabel(status: string | null) {
  switch (status) {
    case 'ready': return 'Ready';
    case 'under_construction': return 'Under Construction';
    case 'upcoming': return 'Upcoming';
    default: return status;
  }
}

export function ProjectAdsSidebar() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { displayCurrency, rate } = useCurrency();

  const { data: projects, isLoading } = useQuery({
    queryKey: ['sidebar-projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, city, sub_city, price_min, price_max, currency, status, total_units, available_units, slug, is_featured, developer_id, project_images(url, is_primary), developer_profiles(company_name, logo_url)')
        .eq('approval_status', 'approved')
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      return data as unknown as ProjectWithImages[];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-64 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!projects?.length) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold text-sm">New Projects</h3>
      </div>

      {projects.map((project) => {
        const name = typeof project.name === 'object' ? (project.name as any)?.[lang] || (project.name as any)?.en : project.name;
        const primaryImage = project.project_images?.find(img => img.is_primary)?.url || project.project_images?.[0]?.url;
        const devName = project.developer_profiles?.company_name;
        const currency = project.currency || 'ETB';

        return (
          <Link
            key={project.id}
            to={`/projects/${(project as any).slug || project.id}`}
            className="block group rounded-xl border bg-card overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Image */}
            <div className="relative aspect-[16/10] overflow-hidden bg-muted">
              {primaryImage ? (
                <img
                  src={primaryImage}
                  alt={name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Building2 className="h-10 w-10 text-muted-foreground/30" />
                </div>
              )}

              {/* Status badge */}
              <Badge className={`absolute top-2 left-2 text-[10px] text-white border-0 ${getStatusColor(project.status)}`}>
                {getStatusLabel(project.status)}
              </Badge>

              {project.is_featured && (
                <Badge className="absolute top-2 right-2 text-[10px] bg-primary text-primary-foreground border-0">
                  Featured
                </Badge>
              )}

              {/* Price overlay */}
              {(project.price_min || project.price_max) && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-6">
                  <p className="text-white font-bold text-sm">
                    {project.price_min && project.price_max
                      ? `${formatPriceCompact(project.price_min, currency, displayCurrency, rate)} – ${formatPriceCompact(project.price_max, currency, displayCurrency, rate)}`
                      : `From ${formatPriceCompact(project.price_min || project.price_max, currency, displayCurrency, rate)}`
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-3 space-y-2">
              <h4 className="font-semibold text-sm leading-tight line-clamp-1 group-hover:text-primary transition-colors">
                {name || 'Untitled Project'}
              </h4>

              <div className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="text-[11px] line-clamp-1">
                  {[project.sub_city, project.city].filter(Boolean).join(', ')}
                </span>
              </div>

              {devName && (
                <div className="flex items-center gap-1.5">
                  {project.developer_profiles?.logo_url ? (
                    <img src={project.developer_profiles.logo_url} alt="" className="h-4 w-4 rounded-full object-cover" />
                  ) : (
                    <Building2 className="h-3 w-3 text-muted-foreground" />
                  )}
                  <span className="text-[11px] text-muted-foreground">by {devName}</span>
                </div>
              )}

              {project.available_units != null && project.total_units != null && (
                <p className="text-[11px] text-muted-foreground">
                  {project.available_units} of {project.total_units} units available
                </p>
              )}

              {/* CTA buttons */}
              <div className="flex gap-1.5 pt-1">
                <Button size="sm" className="h-7 text-[10px] flex-1 gap-1">
                  <Phone className="h-3 w-3" /> Call
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px] flex-1 gap-1">
                  <Mail className="h-3 w-3" /> Email
                </Button>
              </div>
            </div>
          </Link>
        );
      })}

      {/* Alert CTA */}
      <div className="rounded-xl border bg-muted/50 p-4 text-center space-y-2">
        <Bell className="h-5 w-5 mx-auto text-primary" />
        <p className="text-xs font-medium">Get notified about new projects</p>
        <Button variant="outline" size="sm" className="h-8 text-xs w-full">
          Alert Me
        </Button>
      </div>
    </div>
  );
}
